import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";

import { getChildNode, shouldEditNode } from "./utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { InlineEditableNode } from "@/models/nodes/visitors/NodeVisitor";

export function autoEditNode(doc: DocumentModel, direction: "left" | "right") {
  const { editingNode } = doc.input;

  if (editingNode?.isInlineEditable()) {
    return handleEditingNode(editingNode, direction);
  }

  const node = direction === "right" ? doc.cursor.nodeAfterCursor : doc.cursor.nodeBeforeCursor;

  if (node?.isInlineEditable()) {
    return handleEditableNode(node, direction);
  }

  /**
   * Handle currently focused children of inline editable node.
   * Mark parent node as editing if needed.
   *
   * For example:
   * - sin(|x)
   * - {ArrowLeft} -> ~sin|(x)~
   *
   * - sin^|2()
   * - {ArrowLeft} -> ~sin|^2(x)~
   */
  if (!node) {
    const parent = doc.cursor.node?.ancestors.find((n) => n.isInlineEditable());

    if (parent?.isInlineEditable() && shouldEditNode(parent, direction)) {
      doc.cursor.moveBeforeNode(parent);
      doc.input.editNode(parent, direction === "right" ? 0 : parent.value.length);

      return true;
    }
  }

  return false;
}

/**
 * Handle editing node.
 *
 * If node has children, move cursor to the appropriate child.
 * If sibling editable node has children, skip editing.
 * Otherwise, enter edit mode for sibling node.
 */
function handleEditingNode(
  editingNode: NodeModel & InlineEditableNode,
  direction: "left" | "right",
) {
  const { doc } = editingNode;
  const { inputSelection } = doc.input;

  if (!inputSelection || (inputSelection.from > 1 && direction === "left")) {
    return false;
  }

  if (inputSelection.to === inputSelection.from) {
    const childNode = getChildNode(editingNode, direction, "editing");

    if (childNode) {
      const [inlineNode, index] = childNode;

      doc.cursor.moveTo(inlineNode, index);
      doc.editor.resetInput(true);

      return true;
    }

    const sibling =
      direction === "right" ? editingNode.nextSiblings[0] : editingNode.prevSiblings[0];

    if (sibling?.isInlineEditable()) {
      const childSiblingNode = getChildNode(sibling, direction, "idle");

      if (childSiblingNode) {
        return false;
      }

      doc.input.editNode(sibling, direction === "right" ? 0 : sibling.value.length);
      return true;
    }

    if (direction === "left") {
      doc.cursor.moveBeforeNode(editingNode);
      doc.editor.resetInput(true);
      return true;
    }

    doc.cursor.moveAfterNode(editingNode);
    return false;
  }
}

/**
 * Handle editable node.
 *
 * If node has children, move cursor to the appropriate child.
 * Otherwise, enter edit mode.
 */
function handleEditableNode(node: NodeModel & InlineEditableNode, direction: "left" | "right") {
  const { doc } = node;
  const childNode = getChildNode(node, direction, "idle");

  if (childNode) {
    const [inlineNode, index] = childNode;

    doc.cursor.moveTo(inlineNode, index);
    doc.editor.resetInput(true);

    return true;
  }

  const skipNumberEdit =
    node.is(NumberNodeModel) &&
    !node.unitNode &&
    !(node.power || node.subscript) &&
    node.value.length === 1;

  if (skipNumberEdit) {
    return false;
  }

  doc.cursor.moveBeforeNode(node);
  doc.input.editNode(node, direction === "right" ? 1 : node.value.length - 1);

  return true;
}
